import React, { useState, useEffect } from "react"

export default function AppPage() {
  const [show, setShow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasVoted, setHasVoted] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [hasSubmittedToShow, setHasSubmittedToShow] = useState(false)
  const [suggestions, setSuggestions] = useState({})
  const [selectedGames, setSelectedGames] = useState([])
  const [tapCount, setTapCount] = useState(0)

  // Check if user has already submitted to this show (cookie-based)
  const checkSubmissionCookie = (showId) => {
    if (!showId) return false
    return document.cookie.includes(`phatask_submitted_${showId}=true`)
  }

  // Set submission cookie for this show
  const setSubmissionCookie = (showId) => {
    if (!showId) return
    document.cookie = `phatask_submitted_${showId}=true; path=/; max-age=86400`
  }

  // Clear submission cookie (for secret 5-tap)
  const clearSubmissionCookie = (showId) => {
    if (!showId) return
    document.cookie = `phatask_submitted_${showId}=; path=/; max-age=0`
  }

  useEffect(() => {
    fetchCurrentShow()
    checkVotingStatus()
  }, [])

  // Poll for show status changes every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCurrentShow()
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  const fetchCurrentShow = async () => {
    try {
      const response = await fetch("/api/current_show")
      const data = await response.json()
      setShow(data)
      // Check if user has already submitted to this show
      if (data && data.id) {
        setHasSubmittedToShow(checkSubmissionCookie(data.id))
      }
      setLoading(false)
    } catch (error) {
      console.error("Error fetching show:", error)
      setLoading(false)
    }
  }

  const checkVotingStatus = async () => {
    try {
      const response = await fetch("/api/votes/check")
      const data = await response.json()
      setHasVoted(data.has_voted)
    } catch (error) {
      console.error("Error checking voting status:", error)
    }
  }

  const handleSuggestionChange = (askForId, value) => {
    setSuggestions({ ...suggestions, [askForId]: value })
  }

  const handleSubmitSuggestions = async (e) => {
    e.preventDefault()

    try {
      // Submit each suggestion
      for (const [askForId, content] of Object.entries(suggestions)) {
        if (content && content.trim()) {
          await fetch("/api/suggestions", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
            },
            body: JSON.stringify({
              suggestion: {
                ask_for_id: askForId,
                content: content.trim(),
              },
            }),
          })
        }
      }

      // Only set cookie if ALL second-half askfors have submissions
      // This allows users to submit again during intermission if they left second-half suggestions empty
      if (show && show.id) {
        const secondHalfAskFors = (show.ask_fors || []).filter(af => af.game_half === "second")
        const allSecondHalfFilled = secondHalfAskFors.every(af => {
          const content = suggestions[af.id]
          return content && content.trim()
        })

        // Set cookie only if there are no second-half askfors OR all are filled
        if (secondHalfAskFors.length === 0 || allSecondHalfFilled) {
          setSubmissionCookie(show.id)
          setHasSubmittedToShow(true)
        }
      }
      setSubmitted(true)
    } catch (error) {
      console.error("Error submitting suggestions:", error)
      alert("Error submitting suggestions")
    }
  }

  const toggleGameSelection = (gameId) => {
    if (selectedGames.includes(gameId)) {
      setSelectedGames(selectedGames.filter(id => id !== gameId))
    } else {
      if (selectedGames.length < 3) {
        setSelectedGames([...selectedGames, gameId])
      }
    }
  }

  // Secret 5-tap to clear submission cookie and allow re-submission
  const handleThankYouTap = () => {
    const newCount = tapCount + 1
    setTapCount(newCount)
    if (newCount >= 5) {
      if (show && show.id) {
        clearSubmissionCookie(show.id)
        setHasSubmittedToShow(false)
        setSubmitted(false)
        setSuggestions({})
      }
      setTapCount(0)
    }
  }

  const handleSubmitVotes = async () => {
    try {
      const response = await fetch("/api/votes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({ game_ids: selectedGames }),
      })

      if (response.ok) {
        setHasVoted(true)
      } else {
        const data = await response.json()
        alert(data.error || "Error submitting votes")
      }
    } catch (error) {
      console.error("Error submitting votes:", error)
      alert("Error submitting votes")
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    )
  }

  if (!show) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">No Active Show</h1>
          <p className="text-gray-600">There is currently no active show. Please check back later!</p>
        </div>
      </div>
    )
  }

  // State 1: Pre-show (Waiting to begin)
  if (show.status === "Waiting to begin") {
    // Show thank you if user already submitted (via cookie or just now)
    if (submitted || hasSubmittedToShow) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center p-8">
          <div className="bg-white rounded-lg shadow-2xl p-12 max-w-2xl text-center">
            <h1
              className="text-4xl font-bold text-gray-800 mb-4 cursor-default select-none"
              onClick={handleThankYouTap}
            >
              Thank You!
            </h1>
            <p className="text-xl text-gray-600">Thanks for contributing to the show!</p>
          </div>
        </div>
      )
    }

    const askFors = show.ask_fors || []

    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500 to-blue-600 p-2 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-2xl p-4 md:p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{show.title}</h1>
            <p className="text-xl text-gray-600 mb-8">Help us prepare for the show!</p>

            {askFors.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No suggestions needed at this time.</p>
            ) : (
              <form onSubmit={handleSubmitSuggestions} className="space-y-6">
                {askFors.map((askFor) => (
                  <div key={askFor.id}>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      {askFor.prompt}
                    </label>
                    <textarea
                      rows="3"
                      value={suggestions[askFor.id] || ""}
                      onChange={(e) => handleSuggestionChange(askFor.id, e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base resize-none"
                      placeholder="Your suggestion..."
                    />
                  </div>
                ))}

                <button
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold text-xl transition-colors"
                >
                  Submit Suggestions
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    )
  }

  // State 2: Enjoy the show (First half or Second half)
  if (show.status === "First half" || show.status === "Second half") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-900 mb-4" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.8)'}}>
            Enjoy This Part of the Show!
          </h1>
          <p className="text-2xl text-gray-900 opacity-90">🎭 ✨</p>
        </div>
      </div>
    )
  }

  // State 3: Intermission - Second half submissions or Voting
  if (show.status === "Intermission") {
    // If user hasn't submitted yet, show second-half submission form
    if (!submitted && !hasSubmittedToShow) {
      const secondHalfAskFors = show.second_half_ask_fors || []

      if (secondHalfAskFors.length > 0) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-green-500 to-teal-600 p-2 md:p-8">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg shadow-2xl p-4 md:p-8">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">Intermission Suggestions</h1>
                <p className="text-xl text-gray-600 mb-8">Help us prepare for the second half!</p>

                <form onSubmit={handleSubmitSuggestions} className="space-y-6">
                  {secondHalfAskFors.map((askFor) => (
                    <div key={askFor.id}>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        {askFor.prompt}
                        <span className="text-sm text-gray-500 ml-2">({askFor.game_type})</span>
                      </label>
                      <textarea
                        rows="3"
                        value={suggestions[askFor.id] || ""}
                        onChange={(e) => handleSuggestionChange(askFor.id, e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-none"
                        placeholder="Your suggestion..."
                      />
                    </div>
                  ))}

                  <button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-xl transition-colors"
                  >
                    Submit Suggestions
                  </button>
                </form>
              </div>
            </div>
          </div>
        )
      }
    }

    // If submitted or no second-half askfors, show voting
    if (hasVoted) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center p-8">
          <div className="bg-white rounded-lg shadow-2xl p-12 max-w-2xl text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Thanks for Voting!</h1>
            <div className="text-gray-700">
              <p className="text-lg font-semibold mb-2">Follow us on <a href="https://www.facebook.com/profile.php?id=61583021894806" className="text-blue-600 underline hover:text-blue-700">Facebook</a> for upcoming shows</p>
            </div>
            <p className="text-xl text-gray-600 mb-8">
               Visit <a href="https://www.askforimprov.com" className="text-blue-600 underline hover:text-blue-700">www.askforimprov.com</a> for improv classes!
            </p>
          </div>
        </div>
      )
    }

    // Only show first-half games for voting
    const games = (show.games || [])
      .filter(g => g.half === "first" || !g.half)
      .sort((a, b) => a.order_in_show - b.order_in_show)

    // Show thank you + voting for those who submitted
    if (submitted || hasSubmittedToShow) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <h1
                className="text-4xl font-bold text-gray-800 mb-2 cursor-default select-none"
                onClick={handleThankYouTap}
              >
                Thank You for Your Suggestions!
              </h1>
              <p className="text-xl text-gray-600 mb-8">While you wait, vote for your favorite games from the first half!</p>

              {games.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No games to vote on yet.</p>
              ) : (
                <div className="space-y-4 mb-8">
                  {games.map((game) => (
                    <div
                      key={game.id}
                      onClick={() => toggleGameSelection(game.id)}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedGames.includes(game.id)
                          ? "border-cyan-500 bg-cyan-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`mt-1 w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedGames.includes(game.id)
                            ? "border-cyan-500 bg-cyan-500"
                            : "border-gray-300"
                        }`}>
                          {selectedGames.includes(game.id) && (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800">
                            {game.game_type}
                          </h3>
                          {game.what_happened && (
                            <p className="text-gray-600 mt-2">{game.what_happened}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {games.length > 0 && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    {selectedGames.length} of 3 games selected
                  </p>
                  <button
                    onClick={handleSubmitVotes}
                    disabled={selectedGames.length === 0}
                    className={`px-12 py-4 rounded-lg font-semibold text-xl transition-colors ${
                      selectedGames.length === 0
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-cyan-500 hover:bg-cyan-600 text-white"
                    }`}
                  >
                    Submit Votes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }

    // Fall through to voting if user didn't have any second-half askfors to submit
    // Only show first-half games for voting
    const gamesForVoting = (show.games || [])
      .filter(g => g.half === "first" || !g.half)
      .sort((a, b) => a.order_in_show - b.order_in_show)

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-2xl p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Vote for Your Favorites!</h1>
            <p className="text-xl text-gray-600 mb-8">Select up to 3 games that you enjoyed most</p>

            {gamesForVoting.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No games to vote on yet.</p>
            ) : (
              <div className="space-y-4 mb-8">
                {gamesForVoting.map((game) => (
                  <div
                    key={game.id}
                    onClick={() => toggleGameSelection(game.id)}
                    className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedGames.includes(game.id)
                        ? "border-cyan-500 bg-cyan-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`mt-1 w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedGames.includes(game.id)
                          ? "border-cyan-500 bg-cyan-500"
                          : "border-gray-300"
                      }`}>
                        {selectedGames.includes(game.id) && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-800">
                          {game.game_type}
                        </h3>
                        {game.what_happened && (
                          <p className="text-gray-600 mt-2">{game.what_happened}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {gamesForVoting.length > 0 && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  {selectedGames.length} of 3 games selected
                </p>
                <button
                  onClick={handleSubmitVotes}
                  disabled={selectedGames.length === 0}
                  className={`px-12 py-4 rounded-lg font-semibold text-xl transition-colors ${
                    selectedGames.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-cyan-500 hover:bg-cyan-600 text-white"
                  }`}
                >
                  Submit Votes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // State 4: Complete - Voting only
  if (show.status === "Complete") {
    if (hasVoted) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center p-8">
          <div className="bg-white rounded-lg shadow-2xl p-12 max-w-2xl text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Thanks for Voting!</h1>
            <div className="text-gray-700">
              <p className="text-lg font-semibold mb-2">Follow us on <a href="https://www.facebook.com/profile.php?id=61583021894806" className="text-blue-600 underline hover:text-blue-700">Facebook</a> for upcoming shows</p>
            </div>
            <p className="text-xl text-gray-600 mb-8">
               Visit <a href="https://www.askforimprov.com" className="text-blue-600 underline hover:text-blue-700">www.askforimprov.com</a> for improv classes!
            </p>
          </div>
        </div>
      )
    }

    // Only show first-half games for voting
    const games = (show.games || [])
      .filter(g => g.half === "first" || !g.half)
      .sort((a, b) => a.order_in_show - b.order_in_show)

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-2xl p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Vote for Your Favorites!</h1>
            <p className="text-xl text-gray-600 mb-8">Select up to 3 games that you enjoyed most</p>

            {games.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No games to vote on yet.</p>
            ) : (
              <div className="space-y-4 mb-8">
                {games.map((game) => (
                  <div
                    key={game.id}
                    onClick={() => toggleGameSelection(game.id)}
                    className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedGames.includes(game.id)
                        ? "border-cyan-500 bg-cyan-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`mt-1 w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedGames.includes(game.id)
                          ? "border-cyan-500 bg-cyan-500"
                          : "border-gray-300"
                      }`}>
                        {selectedGames.includes(game.id) && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-800">
                          {game.game_type}
                        </h3>
                        {game.what_happened && (
                          <p className="text-gray-600 mt-2">{game.what_happened}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {games.length > 0 && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  {selectedGames.length} of 3 games selected
                </p>
                <button
                  onClick={handleSubmitVotes}
                  disabled={selectedGames.length === 0}
                  className={`px-12 py-4 rounded-lg font-semibold text-xl transition-colors ${
                    selectedGames.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-cyan-500 hover:bg-cyan-600 text-white"
                  }`}
                >
                  Submit Votes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Fallback
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Show Status Unknown</h1>
        <p className="text-gray-600">Please check back later!</p>
      </div>
    </div>
  )
}
